import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { EmptyState } from '../dashboard/states';
import Badge from './Badge';
import SmsOutreachService from '../../services/SmsOutreachService';
import { fmtDate, fmtDateTime, fmtNumber, fmtTime } from './format';

const OUTCOMES = [
  { id: 'all', label: 'All' },
  { id: 'sent', label: 'Sent' },
  { id: 'failed', label: 'Failed' },
  { id: 'pending', label: 'Unconfirmed' },
];

const PAGE_SIZE = 25;

function outcomeOf(m) {
  if (['accepted', 'delivered'].includes(m.finalStatus)) return 'sent';
  if (['failed', 'rejected', 'undeliverable'].includes(m.finalStatus)) return 'failed';
  return 'pending';
}

/**
 * Server-paged list of individual outbound messages for the selected range.
 * Phones are masked for non-admin roles by the API.
 */
export default function MessagesTable({ range, mode, runId, onClearRun }) {
  const [outcome, setOutcome] = useState('all');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const reqId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => setPage(1), [range.from, range.to, mode, outcome, debouncedQ, runId]);

  useEffect(() => {
    const id = ++reqId.current;
    setLoading(true);
    setError('');
    SmsOutreachService.messages({
      from: range.from,
      to: range.to,
      mode,
      outcome,
      runId: runId || undefined,
      q: debouncedQ || undefined,
      page,
      limit: PAGE_SIZE,
    })
      .then((res) => id === reqId.current && setData(res))
      .catch((e) => id === reqId.current && setError(e.response?.data?.error || e.message || 'Failed to load messages'))
      .finally(() => id === reqId.current && setLoading(false));
  }, [range.from, range.to, mode, outcome, debouncedQ, page, runId]);

  const rows = useMemo(() => data.items || [], [data]);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="flex items-center rounded-lg bg-zinc-100 p-0.5">
          {OUTCOMES.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setOutcome(o.id)}
              className={`h-7 rounded-md px-2.5 text-xs font-medium transition-colors ${outcome === o.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[220px] flex-1">
          <FiSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Confirmation id, last digits of phone, company…"
            className="h-8 w-full rounded-lg border border-zinc-200 bg-white pl-8 pr-2 text-xs outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
          />
        </div>
        {runId ? (
          <button
            type="button"
            onClick={onClearRun}
            className="inline-flex h-7 items-center gap-1 rounded-md bg-indigo-50 px-2 text-[11px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20 hover:bg-indigo-100"
          >
            run: <span className="font-mono">{runId}</span> ×
          </button>
        ) : null}
        <span className="text-[11px] text-zinc-500">{fmtNumber(data.total)} messages</span>
      </div>

      <div className="flex-1 overflow-auto rounded-lg border border-zinc-200">
        <table className="min-w-full text-left text-xs">
          <thead className="sticky top-0 z-10 bg-zinc-50 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-3 py-2">Sent (PT)</th>
              <th className="px-3 py-2">Guest phone</th>
              <th className="px-3 py-2">CID</th>
              <th className="px-3 py-2">Stay</th>
              <th className="px-3 py-2">Outcome</th>
              <th className="px-3 py-2">Mode</th>
              <th className="px-3 py-2">Template</th>
              <th className="px-3 py-2 text-right">Seg</th>
              <th className="px-3 py-2">Run</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 bg-white">
            {loading && !rows.length ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={9} className="px-3 py-2">
                    <div className="h-4 w-full animate-pulse rounded bg-zinc-50" />
                  </td>
                </tr>
              ))
            ) : error ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-rose-600">
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <EmptyState title="No messages" description="Nothing matches the current filters." />
                </td>
              </tr>
            ) : (
              rows.map((m) => {
                const oc = outcomeOf(m);
                const open = expanded === m.id;
                const sentAt = m.acceptedAt || m.createdAt;
                return (
                  <React.Fragment key={m.id}>
                    <tr onClick={() => setExpanded(open ? null : m.id)} className={`cursor-pointer hover:bg-zinc-50 ${open ? 'bg-zinc-50' : ''} ${loading ? 'opacity-60' : ''}`}>
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="font-medium tabular-nums text-zinc-900">{fmtTime(sentAt)}</div>
                        <div className="text-[10px] text-zinc-400">{fmtDate(sentAt)} PT</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-zinc-800">{m.phone || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-zinc-700">{m.confirmationId || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-zinc-700">
                        {m.checkInDate ? `${m.checkInDate}${m.checkOutDate ? ` → ${m.checkOutDate}` : ''}` : '—'}
                        {m.daysUntilArrival != null ? <span className="ml-1 text-[10px] text-zinc-400">D-{m.daysUntilArrival}</span> : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <Badge tone={oc === 'sent' ? 'emerald' : oc === 'failed' ? 'rose' : 'zinc'}>{m.finalStatus || 'queued'}</Badge>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <Badge tone={m.mode === 'live' ? 'rose' : 'sky'}>{m.mode === 'live' ? 'LIVE' : 'dry'}</Badge>
                      </td>
                      <td className="max-w-[160px] truncate px-3 py-2 text-zinc-600" title={m.templateKey || ''}>
                        {m.templateKey || '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-zinc-600">{m.segments ?? '—'}</td>
                      <td className="max-w-[140px] truncate px-3 py-2 font-mono text-[10px] text-zinc-400" title={m.runId || ''}>
                        {m.runId || '—'}
                      </td>
                    </tr>
                    {open ? (
                      <tr className="bg-zinc-50/70">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="grid gap-3 laptop:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
                            <pre className="whitespace-pre-wrap rounded-lg border border-zinc-200 bg-white p-3 font-sans text-[11.5px] leading-relaxed text-zinc-800">
                              {m.body || '(no body stored)'}
                            </pre>
                            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
                              <dt className="text-zinc-500">Provider uuid</dt>
                              <dd className="truncate font-mono text-zinc-700" title={m.providerMessageUuid || ''}>{m.providerMessageUuid || '—'}</dd>
                              <dt className="text-zinc-500">Accepted</dt>
                              <dd className="text-zinc-700">{fmtDateTime(m.acceptedAt)}</dd>
                              <dt className="text-zinc-500">Finalized</dt>
                              <dd className="text-zinc-700">{fmtDateTime(m.finalizedAt)}</dd>
                              <dt className="text-zinc-500">Encoding</dt>
                              <dd className="text-zinc-700">{m.encoding || '—'}</dd>
                              <dt className="text-zinc-500">Link type</dt>
                              <dd className="text-zinc-700">{m.linkType || '—'}</dd>
                              <dt className="text-zinc-500">Trigger</dt>
                              <dd className="text-zinc-700">{m.trigger || '—'}</dd>
                              {m.errorText ? (
                                <>
                                  <dt className="text-rose-600">Error</dt>
                                  <dd className="text-rose-700">
                                    {m.errorCode ? `${m.errorCode} · ` : ''}
                                    {m.errorText}
                                  </dd>
                                </>
                              ) : null}
                            </dl>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {data.totalPages > 1 ? (
        <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
          <span>
            Page {page} of {data.totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-md border border-zinc-200 bg-white px-2 py-1 hover:bg-zinc-50 disabled:opacity-40">
              Prev
            </button>
            <button type="button" disabled={page >= data.totalPages || loading} onClick={() => setPage((p) => p + 1)} className="rounded-md border border-zinc-200 bg-white px-2 py-1 hover:bg-zinc-50 disabled:opacity-40">
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
